import type { components } from '@fluencelabs/vodopad-admin-api'
import { getDefaultFilter, useTable } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Breadcrumb, Form, Input, Select, Space, Table } from 'antd'

import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { StatusTag } from '@/components/common/StatusTag'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { UserSearchSelect } from '@/components/common/UserSearchSelect'
import { VPC_STATUSES } from '@/constants/statuses'
import { useClusters } from '@/hooks/useClusters'
import { useTableRowNavigation } from '@/hooks/useTableRowNavigation'
import { UsersCacheProvider } from '@/hooks/useUsersByIds'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'
import { searchByNameOrId } from '@/utils/searchFilters'

type UserVpcDto = components['schemas']['UserVpcDto']

export default function VpcsListPage() {
  const onRow = useTableRowNavigation('vpcs')
  const clusters = useClusters()

  const { tableProps, searchFormProps, filters } = useTable<
    UserVpcDto,
    HttpError,
    { search?: string; status?: string; userId?: string; clusterId?: string }
  >({
    resource: 'vpcs',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      ...searchByNameOrId(values.search, 'ids', true),
      { field: 'status', operator: 'eq', value: values.status || undefined },
      { field: 'userId', operator: 'eq', value: values.userId || undefined },
      {
        field: 'clusterId',
        operator: 'eq',
        value: values.clusterId || undefined,
      },
    ],
  })

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'VPCs' }]} />
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16 }}
        initialValues={{
          search:
            getDefaultFilter('name', filters, 'eq') ??
            getDefaultFilter('ids', filters, 'eq'),
          status: getDefaultFilter('status', filters, 'eq'),
          clusterId: getDefaultFilter('clusterId', filters, 'eq'),
        }}
      >
        <Space wrap>
          <Form.Item name="search" noStyle>
            <Input.Search
              placeholder="Search by name or ID..."
              style={{ width: 250 }}
              onSearch={() => searchFormProps.form?.submit()}
              allowClear
            />
          </Form.Item>
          <Form.Item name="status" noStyle>
            <Select
              placeholder="Status"
              style={{ width: 140 }}
              allowClear
              onChange={() => searchFormProps.form?.submit()}
              options={VPC_STATUSES.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>
          <Form.Item name="userId" noStyle>
            <UserSearchSelect onChange={() => searchFormProps.form?.submit()} />
          </Form.Item>
          <Form.Item name="clusterId" noStyle>
            <Select
              placeholder="Cluster"
              style={{ width: 200 }}
              allowClear
              showSearch
              optionFilterProp="label"
              onChange={() => searchFormProps.form?.submit()}
              options={clusters.map((c) => ({
                label: c.name ?? c.id,
                value: c.id,
              }))}
            />
          </Form.Item>
        </Space>
      </Form>

      <UsersCacheProvider
        ids={(tableProps.dataSource ?? []).map((row) => row.userId)}
      >
        <Table
          {...tableProps}
          pagination={tablePaginationConfig(tableProps)}
          rowKey="id"
          onRow={onRow}
          columns={[
            { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (s: string) => <StatusTag status={s} />,
            },
            {
              title: 'User',
              dataIndex: 'userId',
              key: 'userId',
              ellipsis: true,
              render: (id: string) =>
                id ? <UserEmailLink userId={id} /> : '—',
            },
            {
              title: 'Cluster',
              dataIndex: 'clusterId',
              key: 'clusterId',
              ellipsis: true,
              render: (id: string) =>
                id ? <ClusterNameLink clusterId={id} /> : '—',
            },
            {
              title: 'Subnets',
              dataIndex: 'subnetsCount',
              key: 'subnetsCount',
              width: 80,
            },
            {
              title: 'External',
              dataIndex: 'enableExternal',
              key: 'enableExternal',
              width: 80,
              render: (v: boolean | null) => (v ? 'Yes' : 'No'),
            },
            {
              title: 'Created',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 180,
              render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
            },
          ]}
          scroll={{ x: 800 }}
        />
      </UsersCacheProvider>
    </div>
  )
}
